import { useEffect, useState } from "react";
import Auth from "../Auth";
import { AUTHOR_NATION } from "../../../src/shared/conferenceCampaign";

const columns = ["email", "category", "placement", "enrolledAt", "checkoutStarted", "isTest", "converted", "paidAt", "channel", "planId", "interval", "currency", "amountMinor", "refundedMinor", "period"];
const csvCell = value => `"${String(value ?? "").replace(/^[=+@-]/, "'$&").replace(/"/g, '""')}"`;
const money = (currency, minorAmount) => {
  const format = new Intl.NumberFormat("en-US", { style: "currency", currency });
  return format.format(minorAmount / (10 ** format.resolvedOptions().maximumFractionDigits));
};

export default function ConferenceReport() {
  const [rows, setRows] = useState([]);
  const [includeTests, setIncludeTests] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await Auth.fetchWithIdToken("/api/conference/report");
      if (!result.success) throw new Error(result.message || "Report could not be loaded.");
      setRows(result.rows);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  const visible = rows.filter(row => includeTests || !row.isTest);
  const eligible = visible.filter(row => ["new", "trial"].includes(row.category));
  const conversions = eligible.filter(row => row.firstPayment?.converted);
  const revenue = conversions.reduce((totals, row) => {
    const p = row.firstPayment;
    totals[p.currency] = (totals[p.currency] || 0) + p.amountMinor - p.refundedMinor;
    return totals;
  }, {});
  function download() {
    const content = [columns.join(","), ...visible.map(row => {
      const flat = { ...row, ...row.firstPayment };
      return columns.map(key => csvCell(flat[key])).join(",");
    })].join("\r\n");
    const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${AUTHOR_NATION.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
  return <section style={{ padding: 24, maxWidth: 1400, margin: "auto" }}>
    <h1>Author Nation 2026</h1>
    <p>November 9–14 · Follow-up through November 21 · America/Los_Angeles</p>
    <p>One conversion per new or trial customer’s first paid subscription. Renewals and reactivations are excluded from the conversion rate. Revenue is grouped by currency.</p>
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", margin: "20px 0" }}>
      <button onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      <button onClick={download} disabled={loading}>Download CSV</button>
      <label><input type="checkbox" checked={includeTests} onChange={e => setIncludeTests(e.target.checked)} /> Include rehearsals/test accounts</label>
    </div>
    {error && <p role="alert">{error}</p>}
    <dl style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
      {Object.entries({
        "Eligible accounts": eligible.length,
        "New signups": eligible.filter(r => r.category === "new").length,
        "Existing trial users": eligible.filter(r => r.category === "trial").length,
        "Checkout started": eligible.filter(r => r.checkoutStarted).length,
        "Paid conversions": conversions.length,
        "Conversion rate": eligible.length ? `${(100 * conversions.length / eligible.length).toFixed(1)}%` : "—",
        "During event": conversions.filter(r => r.firstPayment.period === "event").length,
        "Follow-up": conversions.filter(r => r.firstPayment.period === "follow_up").length,
        "Reactivations": visible.filter(r => r.category === "former_subscriber" && r.firstPayment && new Date(r.firstPayment.paidAt) >= new Date(AUTHOR_NATION.startsAt) && new Date(r.firstPayment.paidAt) < new Date(r.attributionEndsAt)).length,
        "Refunded conversions": conversions.filter(r => r.firstPayment.refundedMinor > 0).length,
      }).map(([label, value]) => <div key={label}><dt>{label}</dt><dd style={{ margin: "8px 0", fontSize: 24 }}>{value}</dd></div>)}
    </dl>
    <p>Net first-payment revenue: {Object.entries(revenue).map(([currency, amount]) => money(currency, amount)).join("; ") || "—"}</p>
    <p><a href={AUTHOR_NATION.path}>Open conference landing page</a></p>
    <div style={{ overflowX: "auto" }}><table style={{ width: "100%", textAlign: "left", borderCollapse: "collapse" }}>
      <thead><tr>{["Account", "Entered", "Category", "Placement", "Checkout", "Payment", "Plan", "Conversion"].map(t => <th key={t} style={{ padding: 10 }}>{t}</th>)}</tr></thead>
      <tbody>{visible.map(row => <tr key={row.userId} style={{ borderTop: "1px solid #ccc" }}>
        <td style={{ padding: 10 }}><a href={`/admin/user/${encodeURIComponent(row.email || row.userId)}`}>{row.email || row.userId}</a>{row.isTest && " (test)"}</td>
        <td>{new Date(row.enrolledAt).toLocaleString("en-US", { timeZone: AUTHOR_NATION.timeZone })}</td><td>{row.category}</td><td>{row.placement}</td>
        <td>{row.checkoutStarted ? "Started" : "—"}</td><td>{row.firstPayment?.channel || "—"}</td>
        <td>{row.firstPayment ? `${row.firstPayment.planId} (${row.firstPayment.interval})` : "—"}</td>
        <td>{row.firstPayment?.converted ? row.firstPayment.period : "—"}</td>
      </tr>)}</tbody>
    </table>{!loading && !visible.length && <p>No conference entries yet.</p>}</div>
  </section>;
}
